import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import CategoryCard from '../../components/feature/CategoryCard';
import ContentCard from '../../components/feature/ContentCard';
import Button from '../../components/base/Button';
import { mockPhotos, mockVideos, mockCategories, mockUser } from '../../mocks/content';

export default function HomePage() {
  const navigate = useNavigate();

  const handleCategoryClick = (categoryId: string) => {
    navigate(`/category/${categoryId}`);
  };

  const handleContentClick = (contentId: string) => {
    navigate(`/content/${contentId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-[#1C1C1C] to-[#D4AF37] overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/4cab03cbb5916d5f7a34f741e836b26c.jpg')`
          }}
        ></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-center w-full">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
              صورتي
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8">
              اكتشف أجمل الصور والفيديوهات الأصلية من المملكة العربية السعودية
            </p>
            <Button 
              size="lg" 
              variant="primary"
              onClick={() => navigate('/category/photos')}
            >
              ابدأ التصفح الآن
            </Button>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#1C1C1C] text-center mb-12">
            تصفح حسب الفئة
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {mockCategories.map((category) => (
              <CategoryCard 
                key={category.id} 
                category={category}
                onClick={() => handleCategoryClick(category.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Photos */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl font-bold text-[#1C1C1C]">
              الصور المميزة
            </h2>
            <Button 
              variant="outline"
              onClick={() => navigate('/category/photos')}
            >
              عرض المزيد
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockPhotos.slice(0, 6).map((photo) => (
              <ContentCard 
                key={photo.id} 
                content={photo}
                onClick={() => handleContentClick(photo.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Videos */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl font-bold text-[#1C1C1C]">
              الفيديوهات المميزة
            </h2>
            <Button 
              variant="outline"
              onClick={() => navigate('/category/videos')}
            >
              عرض المزيد
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mockVideos.map((video) => (
              <ContentCard 
                key={video.id} 
                content={video}
                onClick={() => handleContentClick(video.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#1C1C1C] to-[#D4AF37]">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            انضم إلى مجتمع المصورين المحترفين
          </h2>
          <p className="text-xl text-white/90 mb-8">
            اكتشف أعمال أفضل المصورين السعوديين واشترِ المحتوى الأصلي بجودة عالية
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              variant="primary"
              onClick={() => navigate('/login')}
            >
              إنشاء حساب جديد
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#1C1C1C]"
              onClick={() => navigate('/category/photos')}
            >
              تصفح كزائر
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1C1C1C] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-[#D4AF37] to-[#B8941F] rounded-lg flex items-center justify-center ml-2">
                  <i className="ri-camera-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: '"Pacifico", serif' }}>
                  صورتي
                </h3>
              </div>
              <p className="text-gray-400">
                منصة رائدة لبيع الصور والفيديوهات الأصلية في المملكة العربية السعودية
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">الفئات</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-[#D4AF37] cursor-pointer">صور فوتوغرافية</a></li>
                <li><a href="#" className="hover:text-[#D4AF37] cursor-pointer">مقاطع فيديو</a></li>
                <li><a href="#" className="hover:text-[#D4AF37] cursor-pointer">مشاهد طبيعية</a></li>
                <li><a href="#" className="hover:text-[#D4AF37] cursor-pointer">معالم دينية</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">الحساب</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-[#D4AF37] cursor-pointer">تسجيل الدخول</a></li>
                <li><a href="#" className="hover:text-[#D4AF37] cursor-pointer">إنشاء حساب</a></li>
                <li><a href="#" className="hover:text-[#D4AF37] cursor-pointer">ملفاتي</a></li>
                <li><a href="#" className="hover:text-[#D4AF37] cursor-pointer">المفضلة</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">تواصل معنا</h4>
              <ul className="space-y-2 text-gray-400">
                <li>البريد الإلكتروني: info@soorati.sa</li>
                <li>الهاتف: +966 11 123 4567</li>
                <li>العنوان: الرياض، المملكة العربية السعودية</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 صورتي. جميع الحقوق محفوظة.</p>
            <p className="mt-2">
              <a href="https://readdy.ai/?origin=logo" className="hover:text-[#D4AF37] cursor-pointer">
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}